import apiClient from '../api/client';
import {ENDPOINTS} from '../api/endpoints';

export const onlineProcessesService = {

    getSurveys: async (statusFilter = []) => {
        const queryParams = new URLSearchParams();
        statusFilter.forEach((status) => {
            queryParams.append('statusFilter', status);
        });

        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey`,
            statusFilter.length > 0 ? 
                { params: queryParams }
                : undefined
        );
        return response.data;
    },

    getSurveyDetails: async (surveyId) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey/${surveyId}`
        );
        return response.data;
    },

    getReferences: async () => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/references`
        );
        return response.data;
    },

    getSurveyParticipationById: async (id) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${id}`
        );
        return response.data;
    },

    getSplitFiles: async(participationId) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/split-supporting-documents`
        )
        return response.data;
    },

    getSupportingDocuments: async(participationId, personRecordId) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents`
        )
        return response.data;
    },

    getPersonRecords: async(participationId) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records`
        );
        return response.data;
    },

    getPersonRecordById: async(participationId, personRecordId) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}`
        );
        return response.data;
    },

    getSignatoryPersonRecords: async(participationId) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/mainSignatory`
        );
        return response.data;
    },

    getIncomeAndCharges: async(participationId, personRecordId) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/all-incomes-and-charges`
        );
        return response.data;
    },

    getSupportingDocReferences: async() => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/references/SUPPORTING_DOCUMENT`
        );
        return response.data;
    },

    displaySupportingDocumentFile: async(participationId, personRecordId, supportingDocumentId) => {
        let axiosOptions = {
            responseType: 'blob',
            headers: {
                'Accept': 'application/octet-stream',
            }
        };
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents/${supportingDocumentId}/download`,
            null,
            axiosOptions, 
            true
        );

        return response.data;
    },

    impersonate: async(username, context) => {
        const response = await apiClient.get(
            `${ENDPOINTS.ONLINE_PROCESSES}/endusers/${username}/impersonate`,
            {
                params: {
                    context: context
                }
            }
        );
        return response.data;
    },

    uploadSupportingDocumentFile: async({participationId, personRecordId, supportingDocumentId, forceUpload}, body) => {
        const response = await apiClient.put(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents/${supportingDocumentId}/upload`,
            body,
            {
                params: {
                    ...(forceUpload !== undefined && {forceUpload})
                }
            }
        );
        return response.data
    },

    updateSupportingDocument: async({participationId, personRecordId, supportingDocumentId}, body) => {
        const response = await apiClient.put(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents/${supportingDocumentId}`,
            body
        );
        return response.data;
    },

    validateSupportingDocument: async(participationId, personRecordId, supportingDocumentId) => {
        const response = await apiClient.put(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents/${supportingDocumentId}/validate`
        );
        return response.data;
    },

    updateDocumentParent: async(participationId, personRecordId, supportingDocumentId) => {
        const response = await apiClient.put(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents/${supportingDocumentId}/update-documents-parent`
        );
        return response.data;
    },

    updateIncomeAndCharges: async({participationId, personRecordId, incomesAndChargesId}, body) => {
        const response = await apiClient.put(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/incomes-and-charges/${incomesAndChargesId}`,
            body
        );
        return response.data;
    },

    async updateParticipation({participationId}, body){
        const axiosOptions = {
            headers: {
                'Content-Type': 'text/plain',
            }
        }
        const response = await apiClient.put(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}`,
            body,
            axiosOptions
        );
        return response.data;
    },
    
    searchSurveyParticipations: async (criteria) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/search`,
            criteria
        );
        return response.data;
    },

    findCity: async(searchTerm) => {
        const body = {
            searchTerm: searchTerm
        };
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/autoCompleteCity`,
            body
        );
        return response.data;
    },

    findCountry: async (searchTerm) => {
        const body = {
            searchTerm: searchTerm
        };
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/autoCompleteCountry`,
            body
        );
        return response.data;
    },

    createSupportingDocument: async({participationId, personRecordId}, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents`,
            body
        )
        return response.data;
    },

    uploadDocumentToReceptionBox: async(participationId, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/reception-box-upload`,
            body
        );
        return response.data;
    },

    downloadDocumentToReceptionBox: async(participationId, body) => {
        const axiosOptions = {
            responseType: 'arraybuffer',
            headers: {
                'Content-Type': 'application/json'
            }
        }
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/reception-box-download`,
            body,
            axiosOptions
        )
        return response.data;
    },

    analyzeParticipation: async(surveyParticipationId) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${surveyParticipationId}/analyze`
        );
        return response.data;
    },

    createIncomeAndCharges: async({participationId, personRecordId}, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/incomes-and-charges`,
            body
        );
        return response.data;
    },

    getActivityRecords: async({participationId}, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/activity/search`,
            body
        );
        return response.data;
    },

    getAssistanceRecords: async({participationId}, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/assistance/search`,
            body
        );
        return response.data;
    },

    async updateAssistanceRecord({participationId}, body){
        const response = await apiClient.put(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/assistance`,
            body
        );
        return response.data;
    },

    createAssistanceRecord: async({participationId}, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/assistance/create`,
            body
        );
        return response.data;
    },

    createActivityRecord: async({participationId}, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/activity/create`,
            body
        );
        return response.data;
    },

    sendMail: async({surveyParticipationId}, body) => {
        const response = await apiClient.post(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${surveyParticipationId}/send-mail`,
            body,
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
        return response.data;
    },

    patchPersonRecord: async(participationId, personRecordId, jsonPatch) => {
        const response = await apiClient.patch(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}`, jsonPatch,
            {
                headers: {
                    'Content-Type': 'application/json-patch+json'
                }
            }
        );
        return response.data
    },

    deleteSupportingDocument: async({participationId, personRecordId, supportingDocumentId}) => {
        const response = await apiClient.delete(
            `${ENDPOINTS.ONLINE_PROCESSES}/survey-participations/${participationId}/person-records/${personRecordId}/supporting-documents/${supportingDocumentId}/delete-uploaded-document`
        );
        return response.data;
    },
};